import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserGroupIcon, PlusIcon } from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import GroupChatInterface from '../../components/chat/GroupChatInterface';
import { groupChatService, type GroupChat } from '../../services/groupChatService';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

/**
 * Group Chat Page
 * Displays and manages group chats
 */
const GroupChatPage: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [chats, setChats] = useState<GroupChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<GroupChat | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (chatId) {
      loadChat(chatId);
    }
  }, [chatId]);

  const loadChats = async () => {
    try {
      setLoading(true);
      const res = await groupChatService.getMyGroupChats();
      setChats(res.chats || []);
      if (chatId) {
        const chat = res.chats?.find((c) => c.id === chatId);
        if (chat) {
          setSelectedChat(chat);
        }
      }
    } catch (error: any) {
      console.error('Failed to load chats:', error);
      alert(error?.response?.data?.message || 'Failed to load group chats');
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (id: string) => {
    try {
      const res = await groupChatService.getGroupChat(id);
      setSelectedChat(res.chat);
    } catch (error: any) {
      console.error('Failed to load chat:', error);
      alert(error?.response?.data?.message || 'Failed to load group chat');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-gray-400">Loading group chats...</div>
        </div>
      </div>
    );
  }

  if (selectedChat && chatId) {
    return (
      <div className="h-screen flex flex-col">
        <GroupChatInterface
          chatId={chatId}
          currentUserId={user?.id || ''}
          onClose={() => {
            setSelectedChat(null);
            navigate('/dashboard/group-chat');
          }}
          className="flex-1"
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Group Chats</h1>
          <p className="text-gray-600 mt-1">Join conversations with other patrons</p>
        </div>
        <Button leftIcon={<PlusIcon className="w-5 h-5" />}>
          Create Chat
        </Button>
      </div>

      {chats.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No group chats yet</h3>
            <p className="text-gray-600 mb-4">Join group chats to connect with other patrons</p>
            <Button leftIcon={<PlusIcon className="w-5 h-5" />}>
              Create Chat
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {chats.map((chat) => (
            <Card
              key={chat.id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => {
                setSelectedChat(chat);
                navigate(`/dashboard/group-chat/${chat.id}`);
              }}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-patreon-orange rounded-full flex items-center justify-center text-white font-semibold">
                    <UserGroupIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{chat.name}</h3>
                    {chat._count && (
                      <p className="text-sm text-gray-500">
                        {chat._count.members} members • {chat._count.messages} messages
                      </p>
                    )}
                  </div>
                </div>
                {chat.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{chat.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    chat.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {chat.isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupChatPage;

